package fr.frux.feature.treedetail.identity

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.components.InterceptNavigationBack
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.feature.treedetail.TreeDetail
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_identity_sheet_close
import org.jetbrains.compose.resources.stringResource

/**
 * Conteneur des feuilles du bloc Identité : feuille modale en `Compact`, surface qui recouvre la
 * colonne de la fiche en `Expanded` (la carte reste visible à côté). Aucun état local : la feuille
 * ouverte vient de `IdentityUi.sheet`, que Nav3 ne détruit pas.
 */
@Composable
internal fun IdentitySheetHost(
    sheet: IdentitySheet?,
    onDismiss: () -> Unit,
    content: @Composable (IdentitySheet) -> Unit,
) {
    if (sheet == null) return
    if (LocalFruxLayoutMode.current == FruxLayoutMode.Expanded) {
        InterceptNavigationBack(onBack = onDismiss)
        Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
            Column(modifier = Modifier.statusBarsPadding()) { content(sheet) }
        }
    } else {
        FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) { content(sheet) }
    }
}

/** Les trois feuilles, posées sur l'hôte. Le doute d'identification vit au pied de la feuille d'espèce. */
@Composable
internal fun IdentitySheets(
    tree: TreeDetail?,
    sheet: IdentitySheet?,
    controller: TreeIdentityController,
    onReportDoubt: () -> Unit,
    onWithdrawDoubt: () -> Unit,
) {
    IdentitySheetHost(sheet = sheet, onDismiss = controller::dismissSheet) { open ->
        when (open) {
            is IdentitySheet.Species -> {
                CorrectSpeciesSheet(
                    sheet = open,
                    current = tree?.let { currentIdentityLabel(it) }.orEmpty(),
                    controller = controller,
                    doubt = tree?.let(::doubtAction),
                    onDoubt = {
                        controller.dismissSheet()
                        if (tree?.reportedByMe == true) onWithdrawDoubt() else onReportDoubt()
                    },
                )
            }

            is IdentitySheet.Note -> {
                EditNoteSheet(sheet = open, controller = controller)
            }

            is IdentitySheet.History -> {
                HistorySheet(sheet = open, onRetry = controller::retryHistory, onClose = controller::dismissSheet)
            }
        }
    }
}

/**
 * Squelette commun : en-tête fixe (titre, sous-titre, « Fermer »), corps défilant, pied fixe.
 * Marges 0/20/4/20 dp du canvas.
 */
@Composable
internal fun IdentitySheetLayout(
    title: String,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    subtitle: String? = null,
    spacing: Int = 14,
    footer: (@Composable ColumnScope.() -> Unit)? = null,
    body: @Composable ColumnScope.() -> Unit,
) {
    Column(
        modifier = modifier.fillMaxWidth().imePadding().padding(start = 20.dp, end = 20.dp, bottom = 4.dp),
        verticalArrangement = Arrangement.spacedBy(spacing.dp),
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.Top) {
            Column(
                modifier = Modifier.weight(1f).padding(top = 8.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.headlineMedium,
                    color = MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.semantics { heading() },
                )
                if (subtitle != null) {
                    Text(
                        text = subtitle,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
            FruxIconButton(
                icon = Icons.Outlined.Close,
                contentDescription = stringResource(Res.string.treedetail_identity_sheet_close),
                onClick = onClose,
            )
        }
        Column(
            modifier = Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(spacing.dp),
            content = body,
        )
        if (footer != null) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp), content = footer)
        }
    }
}
